/**
 * Standardized icon system — silver-chrome metallic by default, with gold / blue /
 * green / amber / red variants for semantic accents.
 *
 * All icons are 24x24 viewBox SVGs with crisp 2px strokes. Use width/height via size prop.
 */

import { memo, useId } from 'react';

export type IconName =
  | 'lightning'   // trigger
  | 'clock'       // wait / time
  | 'diamond'     // condition
  | 'play'        // action
  | 'sparkle'     // ai / magic
  | 'phone'       // call
  | 'phoneOff'    // missed call
  | 'sms'         // text bubble
  | 'mail'        // email
  | 'storm'       // hail / weather
  | 'invoice'     // money / invoice
  | 'home'        // property / lead
  | 'check'
  | 'x'
  | 'webhook'     // arrow into a circle
  | 'audit'       // clipboard / log
  | 'task'        // checkbox
  | 'calendar'    // schedule
  | 'star'
  | 'gear'
  | 'bell'
  | 'plus'
  | 'minus'
  | 'chevronRight'
  | 'chevronDown'
  | 'chevronLeft'
  | 'save'
  | 'trash'
  | 'arrowRight'
  | 'arrowDown'
  | 'play2'       // small triangle
  | 'pause'
  | 'reload'
  | 'eye'
  | 'help'
  | 'menu'
  | 'search'
  | 'filter'
  | 'sort'
  | 'list'
  | 'grid'
  | 'house'
  | 'user'
  | 'lock'
  | 'sun'
  | 'moon'
  | 'dot';

export type IconVariant = 'silver' | 'gold' | 'blue' | 'green' | 'red' | 'amber' | 'muted';

interface Props {
  name: IconName;
  size?: number;
  variant?: IconVariant;
  className?: string;
  strokeWidth?: number;
}

const GRADIENTS: Record<IconVariant, { top: string; mid: string; bot: string }> = {
  silver: { top: '#f4f5f8', mid: '#c2c5cd', bot: '#7a7d86' },
  gold:   { top: '#fff2cf', mid: '#d4b975', bot: '#8c6f36' },
  blue:   { top: '#c8e8ff', mid: '#6ec6ff', bot: '#2e7caa' },
  green:  { top: '#d3f5e0', mid: '#7be0a3', bot: '#2ea05c' },
  red:    { top: '#ffd0d0', mid: '#f29c9c', bot: '#a04545' },
  amber:  { top: '#fff0c0', mid: '#ffd07a', bot: '#c98b13' },
  muted:  { top: '#cfd0d4', mid: '#9a9ba2', bot: '#5f6068' },
};

function IconImpl({ name, size = 16, variant = 'silver', className, strokeWidth = 1.8 }: Props) {
  const uid = useId();
  const gradId = `iiq-grad-${uid}-${variant}`;
  const highlightId = `iiq-highlight-${uid}`;
  const g = GRADIENTS[variant];

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={gradId} x1="12" y1="2" x2="12" y2="22" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={g.top} />
          <stop offset="45%" stopColor={g.mid} />
          <stop offset="100%" stopColor={g.bot} />
        </linearGradient>
        <linearGradient id={highlightId} x1="12" y1="2" x2="12" y2="12" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>
      </defs>
      <g
        stroke={`url(#${gradId})`}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      >
        {renderPaths(name)}
      </g>
      {/* Soft top highlight for chrome effect on filled shapes */}
      {SOLID_ICONS.has(name) && (
        <g pointerEvents="none">
          {renderPaths(name, { fill: `url(#${gradId})`, stroke: 'none' })}
          {renderHighlight(name, highlightId)}
        </g>
      )}
    </svg>
  );
}

export const Icon = memo(IconImpl);

const SOLID_ICONS = new Set<IconName>([
  'lightning', 'diamond', 'play', 'sparkle', 'star', 'dot', 'play2',
]);

function renderPaths(name: IconName, extra?: { fill?: string; stroke?: string }) {
  const props = extra || {};
  switch (name) {
    case 'lightning':
      return <path d="M13 2L4 13.5h6L11 22l9-11.5h-6L13 2z" fill={props.fill || 'none'} stroke={props.stroke} />;
    case 'clock':
      return (<>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3.5 2" />
      </>);
    case 'diamond':
      return <path d="M12 2.5l9.5 9.5L12 21.5 2.5 12 12 2.5z" fill={props.fill || 'none'} stroke={props.stroke} />;
    case 'play':
      return <path d="M7 4v16l13-8L7 4z" fill={props.fill || 'none'} stroke={props.stroke} />;
    case 'play2':
      return <path d="M9 6v12l9-6-9-6z" fill={props.fill || 'none'} stroke={props.stroke} />;
    case 'sparkle':
      return <path d="M12 2l1.8 5.2L19 9l-5.2 1.8L12 16l-1.8-5.2L5 9l5.2-1.8L12 2zM19 14l.9 2.1L22 17l-2.1.9L19 20l-.9-2.1L16 17l2.1-.9L19 14z" fill={props.fill || 'none'} stroke={props.stroke} />;
    case 'star':
      return <path d="M12 2.5l3 6.4 7 .8-5.3 4.7L18 21l-6-3.6L6 21l1.3-6.6L2 9.7l7-.8 3-6.4z" fill={props.fill || 'none'} stroke={props.stroke} />;
    case 'phone':
      return <path d="M6.4 3.2c.5-.4 1.2-.3 1.6.2l2 2.5c.4.5.4 1.2 0 1.6L8.5 9c.8 1.7 2.2 3.1 3.9 4l1.5-1.5c.4-.4 1.1-.4 1.6 0l2.5 2c.5.4.6 1.1.2 1.6l-1.5 1.8c-.5.6-1.4.8-2.1.5-3.5-1.5-7.5-5.5-9-9-.3-.7-.1-1.6.5-2.1l1.3-1.1z" />;
    case 'phoneOff':
      return (<>
        <path d="M5 3l16 18" />
        <path d="M7 4.5L8.5 7c.4.5.4 1.2 0 1.6L7 10c.8 1.7 2.2 3.1 3.9 4l1.5-1.5c.4-.4 1.1-.4 1.6 0L16.5 14" />
      </>);
    case 'sms':
      return <path d="M3 5a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-9l-5 4v-4H5a2 2 0 01-2-2V5z" />;
    case 'mail':
      return (<>
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="M3 7l9 6 9-6" />
      </>);
    case 'storm':
      return (<>
        <path d="M6 14a4 4 0 010-8 5 5 0 019-1 4 4 0 013 7" />
        <path d="M11 14l-2 4h3l-2 4" />
      </>);
    case 'invoice':
      return (<>
        <path d="M6 3h9l4 4v14l-2-1.5-2 1.5-2-1.5-2 1.5-2-1.5L6 21V3z" />
        <path d="M14 3v4h4" />
        <path d="M9 11h6M9 14h6M9 17h3" />
      </>);
    case 'house':
    case 'home':
      return <path d="M4 11l8-7 8 7v9a1 1 0 01-1 1h-4v-6h-6v6H5a1 1 0 01-1-1v-9z" />;
    case 'check':
      return <path d="M4 12.5l5 5L20 6" />;
    case 'x':
      return <path d="M5 5l14 14M19 5L5 19" />;
    case 'webhook':
      return (<>
        <circle cx="8" cy="8" r="3" />
        <circle cx="16" cy="16" r="3" />
        <path d="M10.5 10l5 5" />
      </>);
    case 'audit':
      return (<>
        <rect x="6" y="3" width="12" height="18" rx="2" />
        <path d="M9 7h6M9 11h6M9 15h4" />
      </>);
    case 'task':
      return (<>
        <rect x="3.5" y="3.5" width="17" height="17" rx="3" />
        <path d="M8 12.5l3 3 5-7" />
      </>);
    case 'calendar':
      return (<>
        <rect x="3" y="5" width="18" height="16" rx="2" />
        <path d="M3 10h18M8 3v4M16 3v4" />
      </>);
    case 'gear':
      return (<>
        <circle cx="12" cy="12" r="3.5" />
        <path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M5 19l2-2M17 7l2-2" />
      </>);
    case 'bell':
      return <path d="M6 16V11a6 6 0 0112 0v5l2 2H4l2-2zM10 21a2 2 0 004 0" />;
    case 'plus':
      return <path d="M12 5v14M5 12h14" />;
    case 'minus':
      return <path d="M5 12h14" />;
    case 'chevronRight':
      return <path d="M9 6l6 6-6 6" />;
    case 'chevronDown':
      return <path d="M6 9l6 6 6-6" />;
    case 'chevronLeft':
      return <path d="M15 6l-6 6 6 6" />;
    case 'save':
      return (<>
        <path d="M5 4h11l3 3v13a1 1 0 01-1 1H5a1 1 0 01-1-1V5a1 1 0 011-1z" />
        <path d="M8 4v5h7V4M8 21v-7h8v7" />
      </>);
    case 'trash':
      return (<>
        <path d="M4 7h16M9 7V4h6v3M6 7l1.5 13a1 1 0 001 1h7a1 1 0 001-1L18 7" />
        <path d="M10 11v6M14 11v6" />
      </>);
    case 'arrowRight':
      return <path d="M5 12h14M13 6l6 6-6 6" />;
    case 'arrowDown':
      return <path d="M12 5v14M6 13l6 6 6-6" />;
    case 'pause':
      return (<>
        <rect x="7" y="5" width="3.5" height="14" rx="1" />
        <rect x="13.5" y="5" width="3.5" height="14" rx="1" />
      </>);
    case 'reload':
      return (<>
        <path d="M4 12a8 8 0 0114-5.3L20 5v5h-5" />
        <path d="M20 12a8 8 0 01-14 5.3L4 19v-5h5" />
      </>);
    case 'eye':
      return (<>
        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
        <circle cx="12" cy="12" r="3" />
      </>);
    case 'help':
      return (<>
        <circle cx="12" cy="12" r="9" />
        <path d="M9.5 9a2.5 2.5 0 015 0c0 2-2.5 2-2.5 4" />
        <path d="M12 17v.5" />
      </>);
    case 'menu':
      return <path d="M4 7h16M4 12h16M4 17h16" />;
    case 'search':
      return (<>
        <circle cx="11" cy="11" r="6" />
        <path d="M15.5 15.5L20 20" />
      </>);
    case 'filter':
      return <path d="M3 5h18l-7 9v6l-4-2v-4L3 5z" />;
    case 'sort':
      return <path d="M5 6h14M7 12h10M9 18h6" />;
    case 'list':
      return <path d="M9 6h11M9 12h11M9 18h11M4.5 6h.01M4.5 12h.01M4.5 18h.01" />;
    case 'grid':
      return (<>
        <rect x="4" y="4" width="6" height="6" rx="1" />
        <rect x="14" y="4" width="6" height="6" rx="1" />
        <rect x="4" y="14" width="6" height="6" rx="1" />
        <rect x="14" y="14" width="6" height="6" rx="1" />
      </>);
    case 'user':
      return (<>
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21c0-4.5 3.5-8 8-8s8 3.5 8 8" />
      </>);
    case 'lock':
      return (<>
        <rect x="4" y="11" width="16" height="10" rx="2" />
        <path d="M8 11V7a4 4 0 018 0v4" />
      </>);
    case 'dot':
      return <circle cx="12" cy="12" r="3" fill={props.fill || 'none'} stroke={props.stroke} />;
    case 'sun':
      return (<>
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
      </>);
    case 'moon':
      return <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />;
    default:
      return null;
  }
}

function renderHighlight(name: IconName, highlightId: string) {
  // Tiny highlight glint on the top of solid icons for chrome polish
  switch (name) {
    case 'lightning':
      return <path d="M13 2L4 13.5h6" fill={`url(#${highlightId})`} opacity="0.35" />;
    case 'diamond':
      return <path d="M12 2.5l9.5 9.5L12 6 2.5 12 12 2.5z" fill={`url(#${highlightId})`} opacity="0.35" />;
    case 'play':
      return <path d="M7 4v8l13-2-13-6z" fill={`url(#${highlightId})`} opacity="0.3" />;
    case 'sparkle':
      return <path d="M12 2l1.8 5.2L19 9l-3 1L12 2z" fill={`url(#${highlightId})`} opacity="0.4" />;
    case 'star':
      return <path d="M12 2.5l3 6.4 7 .8-3 .3-7-.6-3 5.4 3-12.3z" fill={`url(#${highlightId})`} opacity="0.3" />;
    default:
      return null;
  }
}
